import React, { useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaStore, FaSearch } from 'react-icons/fa';
import VenueCard, { getCategoryIcon } from '../../components/VenueCard';

/**
 * VenuesList - Renders the venues grid with categories and scrollbars
 * Extracted from LoyaltyDirectory to reduce file size
 */
function VenuesList({
  companies,
  filteredCompanies,
  sortedGroupedVenues,
  orderedCategories,
  selectedCategory,
  setSelectedCategory,
  venueDistances,
  venueOffers,
  enrollmentStatus,
  loadingEnrollment,
  couponCounts,
  handleViewDeals,
  categoryIconMap,
  hasHydrated,
}) {
  const { t } = useTranslation();
  const [scrollStates, setScrollStates] = useState({});

  // Handle scroll tracking for custom scrollbar
  const handleScroll = useCallback((categoryName, event) => {
    const el = event.target;
    setScrollStates(prev => ({
      ...prev,
      [categoryName]: {
        scrollLeft: el.scrollLeft,
        scrollWidth: el.scrollWidth,
        clientWidth: el.clientWidth,
      }
    }));
  }, []);

  // Initialize scroll measurements when categories render
  useEffect(() => {
    if (!hasHydrated || companies.length === 0) return;

    const measureScrollStates = () => {
      const grids = document.querySelectorAll('.perks-companies-grid');
      const initialStates = {};

      grids.forEach((grid) => {
        const section = grid.closest('.perks-category-section');
        if (section) {
          const categoryName = section.querySelector('.perks-category-header h2')?.textContent.split('(')[0].trim();
          if (categoryName) {
            initialStates[categoryName] = {
              scrollLeft: grid.scrollLeft,
              scrollWidth: grid.scrollWidth,
              clientWidth: grid.clientWidth,
            };
          }
        }
      });

      setScrollStates(initialStates);
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        measureScrollStates();
      });
    });
  }, [hasHydrated, companies, orderedCategories]);

  // Empty state - no venues at all
  if (companies.length === 0) {
    return (
      <div className="perks-no-companies">
        <FaStore className="perks-no-companies-icon" />
        <h3>{t('directory.noVenuesTitle')}</h3>
        <p>{t('directory.noVenuesMessage')}</p>
      </div>
    );
  }

  // Empty state - no matches for filter
  if (filteredCompanies.length === 0) {
    return (
      <div className="perks-no-companies">
        <FaSearch className="perks-no-companies-icon" />
        <h3>{t('directory.noMatchesTitle')}</h3>
        <p>{t('directory.noMatchesMessage')}</p>
        <button
          className="perks-clear-filters-btn"
          onClick={() => setSelectedCategory("All")}
        >
          {t('directory.clearFilters')}
        </button>
      </div>
    );
  }

  // Render filtered results (single category view)
  if (selectedCategory !== "All") {
    return (
      <div className="perks-category-section">
        <div className="perks-companies-grid search-results">
          {filteredCompanies.map((company) => {
            const distance =
              company.distance ??
              venueDistances[company.venue_id] ??
              null;

            return (
              <VenueCard
                key={company.venue_id}
                company={{ ...company, distance }}
                handleViewDeals={handleViewDeals}
                venueOffers={venueOffers}
                enrollmentStatus={enrollmentStatus}
                loadingEnrollment={loadingEnrollment[company.venue_id]}
                couponCount={couponCounts[company.venue_id] || null}
              />
            );
          })}
        </div>
      </div>
    );
  }

  // Render all categories
  return (
    <>
      {orderedCategories.map((categoryName) => {
        const categoryVenues = sortedGroupedVenues[categoryName];
        if (!categoryVenues || categoryVenues.length === 0) return null;

        const scrollState = scrollStates[categoryName] || {
          scrollLeft: 0,
          scrollWidth: 0,
          clientWidth: 0,
        };

        const thumbWidth = scrollState.scrollWidth > 0
          ? (scrollState.clientWidth / scrollState.scrollWidth) * 100
          : 100;
        const thumbLeft = scrollState.scrollWidth > 0
          ? (scrollState.scrollLeft / scrollState.scrollWidth) * 100
          : 0;

        const showScrollbar = scrollState.scrollWidth > scrollState.clientWidth;

        return (
          <div key={categoryName} className="perks-category-section">
            <div className="perks-category-header">
              <span className="perks-category-header-icon">
                {getCategoryIcon(categoryName, categoryIconMap[categoryName])}
              </span>
              <h2>{t(`categories.${categoryName}`, categoryName)}</h2>
              <span className="perks-category-count">
                ({categoryVenues.length})
              </span>
            </div>
            <div className="perks-companies-grid-wrapper">
              <div
                className="perks-companies-grid"
                onScroll={(e) => handleScroll(categoryName, e)}
              >
                {categoryVenues.map((company) => {
                  const distance =
                    company.distance ??
                    venueDistances[company.venue_id] ??
                    null;

                  return (
                    <VenueCard
                      key={company.venue_id}
                      company={{ ...company, distance }}
                      handleViewDeals={handleViewDeals}
                      venueOffers={venueOffers}
                      enrollmentStatus={enrollmentStatus}
                      loadingEnrollment={
                        loadingEnrollment[company.venue_id]
                      }
                      couponCount={couponCounts[company.venue_id] || null}
                    />
                  );
                })}
              </div>
              {showScrollbar && (
                <div className="perks-scrollbar-rail">
                  <div
                    className="perks-scrollbar-thumb"
                    style={{
                      width: `${thumbWidth}%`,
                      left: `${thumbLeft}%`
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        );
      })}
    </>
  );
}

export default VenuesList;
